/* eslint-disable max-len */

import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Alert } from '@bingads-webui-react/alert';
import { LoadingIndicator } from './loading-indicator';
import { ImageListView } from './image-list-view';
import { DataSourceState } from './data-source-def';
import { LoadMoreButton } from './load-more-button';

const baseImagePickerContainerStyle = {
  position: 'relative',
  display: 'inline-block',
  overflowY: 'scroll',
  width: '100%',
};

const getImagePickerContainerStyle = ({ maximizeFrame, height }) => {
  const customizedStyle = maximizeFrame ? { overflowY: 'auto' } : { height };

  return _.extend({}, baseImagePickerContainerStyle, customizedStyle);
};

export class ImagePickerContainer extends React.PureComponent {
  static propTypes = {
    dataSource: PropTypes.shape({
      subscribe: PropTypes.func.isRequired,
      unsubscribe: PropTypes.func.isRequired,
      getData: PropTypes.func.isRequired,
    }).isRequired,
    i18n: PropTypes.shape({
      getString: PropTypes.func.isRequired,
    }).isRequired,
    isMultiSelection: PropTypes.bool,
    onSelectionChanged: PropTypes.func,
    onLoadMore: PropTypes.func,
    maximizeFrame: PropTypes.bool, // expand to fill its parent
    height: PropTypes.number, // not needed if you set maximizeFrame to true
    imagePlaceHolder: PropTypes.func,
    imagePlaceHolderGetter: PropTypes.func,
    imageListItemWrapper: PropTypes.func,
    imageSrcGetter: PropTypes.func,
    isAutoLoadMore: PropTypes.bool,
    isLazyLoadEnabled: PropTypes.bool,
    itemHeight: PropTypes.number,
    useBingAdsLoader: PropTypes.bool,
    maximumItemCount: PropTypes.number,
    isOriginRatioUsed: PropTypes.bool,
    onEscapeFromImage: PropTypes.func,
  };

  static defaultProps = {
    isMultiSelection: false,
    onSelectionChanged: _.noop,
    onLoadMore: null,
    height: 700,
    maximizeFrame: false,
    isAutoLoadMore: false,
    isLazyLoadEnabled: false,
    imagePlaceHolder: null,
    imagePlaceHolderGetter: null,
    imageListItemWrapper: null,
    imageSrcGetter: null,
    itemHeight: 250,
    useBingAdsLoader: false,
    maximumItemCount: null,
    isOriginRatioUsed: false,
    onEscapeFromImage: _.noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      showUiBlocker: false,
      imageList: [],
      selectedIds: [],
      hasMore: false,
    };
  }

  componentDidMount() {
    this.subsriptionId = this.props.dataSource.subscribe((newState) => {
      this.dataSourceChanged(newState);
    });

    this.getImageListAndSet();
  }

  componentWillUnmount() {
    if (this.subsriptionId) {
      this.props.dataSource.unsubscribe(this.subsriptionId);
    }
  }

  onListItemClicked = (id, index) => {
    const idNum = parseInt(id, 10);

    if (!_.find(this.state.imageList, image => image.id === idNum)) {
      return;
    }

    let newSelectedIds = [...this.state.selectedIds];

    if (this.props.isMultiSelection) {
      const existingIndex = newSelectedIds.indexOf(idNum);
      // if exists, remove
      if (existingIndex >= 0) {
        newSelectedIds.splice(existingIndex, 1);
      } else {
        newSelectedIds.push(idNum);
      }
    } else {
      newSelectedIds = [idNum];
    }

    this.setState({
      selectedIds: newSelectedIds,
    });

    // get the data and return
    const selectedData = _.map(
      newSelectedIds,
      selectedItemId => _.find(this.state.imageList, image => image.id === selectedItemId)
    );

    this.props.onSelectionChanged(selectedData, index);
  };

  onImageLoadError = (src) => {
    this.setState(prevState => ({
      imageList: _.reject(prevState.imageList, image => image.src === src),
    }));
  };

  getImageListAndSet = () => {
    const dataSourceData = this.props.dataSource.getData();
    if (this.props.imageSrcGetter) {
      dataSourceData.data.forEach((image) => {
        // eslint-disable-next-line no-param-reassign
        image.src = this.props.imageSrcGetter(image);
        // eslint-disable-next-line no-param-reassign
        image.altText = `${this.props.i18n.getString('ImageExperience_Image')} ${image.id + 1}`;
      });
    }

    this.setState({
      showUiBlocker: false,
      imageList: dataSourceData.data,
      hasMore: dataSourceData.hasMore,
      selectedIds: dataSourceData.data.length > 0 ? this.state.selectedIds : [],
    });
  };

  dataSourceChanged = (newState) => {
    if (newState === DataSourceState.Loading) {
      this.setState({
        showUiBlocker: true,
      });
    } else if (newState === DataSourceState.Done) {
      this.getImageListAndSet();
    }
  };

  render() {
    const {
      imagePlaceHolder,
      imagePlaceHolderGetter,
      imageListItemWrapper,
      isLazyLoadEnabled,
      isMultiSelection,
      itemHeight,
      height,
      maximizeFrame,
      useBingAdsLoader,
      onLoadMore,
      isAutoLoadMore,
      i18n,
      maximumItemCount,
      isOriginRatioUsed,
      onEscapeFromImage,
    } = this.props;

    const {
      imageList,
      selectedIds,
      showUiBlocker,
      hasMore,
    } = this.state;

    const imagePickerContainerStyle = getImagePickerContainerStyle({ height, maximizeFrame });

    const itemExceedsMaximum = _.isNumber(maximumItemCount) && imageList.length > maximumItemCount;
    const displayImageList = itemExceedsMaximum ? imageList.slice(0, maximumItemCount) : imageList;

    return (
      <React.Fragment>
        {itemExceedsMaximum &&
          <Alert
            message={i18n.getString('ImageExperience_SuggestionExceedsMaximum', { imageSize: maximumItemCount })}
            level="Info"
            icon={(<div className="alert-image" />)}
            dismissible={false}
          />
        }
        <div
          className="image-picker-container"
          style={imagePickerContainerStyle}
          aria-describedby="escape-guide"
        >
          <ImageListView
            imageList={displayImageList}
            selectedIds={selectedIds}
            onListItemClicked={this.onListItemClicked}
            isMultiSelection={isMultiSelection}
            isLazyLoadEnabled={isLazyLoadEnabled}
            imageListItemWrapper={imageListItemWrapper}
            imagePlaceHolder={imagePlaceHolder}
            imagePlaceHolderGetter={imagePlaceHolderGetter}
            itemHeight={itemHeight}
            onImageLoadError={this.onImageLoadError}
            i18n={i18n}
            isOriginRatioUsed={isOriginRatioUsed}
            onEscapeFromImage={onEscapeFromImage}
          />
          <span
            className="live-region"
            aria-live="polite"
            role="status"
            aria-label={displayImageList.length > 0 ? i18n.getString('Found {{totalCount}} Results', { totalCount: displayImageList.length }) : i18n.getString('No results found')}
          />
          {
            showUiBlocker && !useBingAdsLoader &&
            <div style={{ width: '100%', height: '200px', textAlign: 'center' }}>
              <LoadingIndicator />
            </div>
          }
          {
            showUiBlocker && useBingAdsLoader &&
            <div style={{ width: '100%', height: '200px', textAlign: 'center' }}>
              {imagePlaceHolder()}
            </div>
          }
          {
            displayImageList.length > 0 && hasMore &&
            <LoadMoreButton
              onLoadMore={onLoadMore}
              isAutoLoadMore={isAutoLoadMore}
            />
          }
          <span id="escape-guide" style={{ display: 'none' }}>
            {i18n.getString('Escape_From_Image_List_Tip')}
          </span>
        </div>
      </React.Fragment>
    );
  }
}
